import { useEffect } from 'react';
import { cx } from './index.tsx';

/**
 * Teclado numérico para el PIN: botones grandes en pantallas táctiles y también
 * acepta el teclado físico (números, Backspace y Enter).
 */
export function PinPad({
  value,
  onChange,
  onSubmit,
  maxLength = 6,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  maxLength?: number;
  disabled?: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (disabled) return;
      const target = e.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      if (/^\d$/.test(e.key) && value.length < maxLength) onChange(value + e.key);
      else if (e.key === 'Backspace') onChange(value.slice(0, -1));
      else if (e.key === 'Enter' && value.length >= 4) onSubmit();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [value, onChange, onSubmit, maxLength, disabled]);

  const press = (digit: string) => {
    if (!disabled && value.length < maxLength) onChange(value + digit);
  };

  // Base sin color de fondo: si dos clases de fondo compiten, gana la que Tailwind generó después
  // (así el botón Entrar quedaba blanco sobre blanco).
  const base = 'h-14 rounded-xl font-semibold disabled:opacity-50';
  const key = cx(base, 'bg-white text-xl ring-1 ring-slate-200 active:bg-slate-100');

  return (
    <div className="space-y-4">
      <div className="flex justify-center gap-3" aria-label={`PIN: ${value.length} dígitos ingresados`}>
        {Array.from({ length: maxLength }, (_, i) => (
          <span key={i} className={cx('size-3.5 rounded-full', i < value.length ? 'bg-brand' : 'bg-slate-300')} />
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
          <button key={d} type="button" className={key} disabled={disabled} onClick={() => press(d)}>
            {d}
          </button>
        ))}
        <button type="button" className={cx(base, 'bg-white text-base ring-1 ring-slate-200 active:bg-slate-100')} disabled={disabled} onClick={() => onChange('')}>
          Borrar
        </button>
        <button type="button" className={key} disabled={disabled} onClick={() => press('0')}>
          0
        </button>
        <button
          type="button"
          className={cx(base, 'bg-brand text-base text-brand-fg active:brightness-95')}
          disabled={disabled || value.length < 4}
          onClick={onSubmit}
        >
          Entrar
        </button>
      </div>
    </div>
  );
}
